export type PaymentIntentStatus =
  | 'requires_payment_method'
  | 'requires_capture'
  | 'succeeded'
  | 'canceled'
  | 'cancelled'
  | 'requires_action'
  | 'processing'
  | 'requires_confirmation'
  | 'requires_source'
  | 'requires_source_action';

// The kiosk-facing terminal decision from the backend. Mirrors the server's
// unified envelope: `outcome` is the success/failure branch, `failure` carries
// the differentiated reason (only when outcome === 'failed').
export type PaymentOutcome =
  | 'pending'
  | 'requires_capture'
  | 'succeeded'
  | 'failed';

export type PaymentFailureCategory =
  | 'card_declined'
  | 'card_error'
  | 'reader_error'
  | 'canceled'
  | 'processing_error'
  | 'unknown';

export interface PaymentFailure {
  category: PaymentFailureCategory;
  // Canonical discriminator: Stripe decline_code, else the error/reader code.
  code: string;
  message?: string | null;
  decline_code?: string | null;
  stripe_code?: string | null;
  // The payment method of the card tap, else its charge. Each tap has a new
  // id, also when the same card fails two times with the same code.
  attempt_id?: string | null;
}

// The kiosk makes these failures itself. The backend never sends them.
export const PAYMENT_TIMEOUT_CODE = 'payment_timeout';
export const MISSING_READER_FAILURE: PaymentFailure = {
  category: 'reader_error',
  code: 'missing_reader_id',
};
export const SESSION_UNAVAILABLE_FAILURE: PaymentFailure = {
  category: 'unknown',
  code: 'session_unavailable',
};
// The backend sends these codes for a reader or an intent that Stripe does
// not know.
export const UNKNOWN_READER_CODE = 'terminal_reader_not_found';
export const UNKNOWN_INTENT_CODE = 'payment_intent_not_found';
// Stripe sends these codes when the reader has no signal for 2 minutes, and
// when the reader does a different action.
export const READER_OFFLINE_CODE = 'terminal_reader_offline';
export const READER_BUSY_CODE = 'terminal_reader_busy';
// The transport gives this code to the 400 of a hold step on a hold that is
// not open: the card window ended and released it, or the order settled.
export const HOLD_NOT_ACCEPTED_CODE = 'hold_not_accepted';

// The transport throws this error when the backend refuses a payment for a
// cause that the visitor must know: before the intent exists, or as the 402,
// 404 or 409 of a failed payment action.
export class PaymentRefusedError extends Error {
  readonly failure: PaymentFailure;

  constructor(failure: PaymentFailure) {
    super(`payment refused: ${failure.code}`);
    this.name = 'PaymentRefusedError';
    this.failure = failure;
  }
}

// The transport throws this error when a send to the reader or a reader
// cancel has no answer: a timeout, a lost connection or a server error. The
// backend can continue that request, thus the reader can do its action.
export class RequestOutcomeUnknownError extends Error {
  constructor(cause: unknown) {
    super('the reader request has no answer');
    this.name = 'RequestOutcomeUnknownError';
    this.cause = cause;
  }
}

export function isRefusedWith(
  error: unknown,
  code: string
): error is PaymentRefusedError {
  return error instanceof PaymentRefusedError && error.failure.code === code;
}

// after_print: the reader holds the amount, the kiosk accepts the payment with
// the hold step, and the backend captures only the printed copies. at_payment:
// the kiosk captures the full amount. An older backend sends no mode.
export type CaptureMode = 'after_print' | 'at_payment';

export interface PaymentIntentDto {
  id: string;
  amount: number;
  currency: string;
  status: PaymentIntentStatus | string;
  metadata?: Record<string, unknown> | null;
  outcome: PaymentOutcome;
  failure?: PaymentFailure | null;
  capture?: CaptureMode | null;
}

export function captureModeOf(
  ...modes: Array<CaptureMode | null | undefined>
): CaptureMode {
  return modes.includes('after_print') ? 'after_print' : 'at_payment';
}

// The answer of the step that accepts the payment. A held amount is paid for
// the kiosk only when the backend captures after the print.
export function isAcceptedPayment(
  intent: PaymentIntentDto,
  mode: CaptureMode | null | undefined
): boolean {
  if (isTerminalSuccess(intent.status)) return true;
  return (
    isCaptureReady(intent.status) &&
    captureModeOf(mode, intent.capture) === 'after_print'
  );
}

const KNOWN_STATUSES: ReadonlySet<string> = new Set<PaymentIntentStatus>([
  'requires_payment_method',
  'requires_capture',
  'succeeded',
  'canceled',
  'cancelled',
  'requires_action',
  'processing',
  'requires_confirmation',
  'requires_source',
  'requires_source_action',
]);

export function isCaptureReady(status: string): boolean {
  return status === 'requires_capture';
}

export function isTerminalSuccess(status: string): boolean {
  return status === 'succeeded';
}

export function isTerminalFailure(status: string): boolean {
  return status === 'canceled' || status === 'cancelled';
}

export function isKnownStatus(status: string): boolean {
  return KNOWN_STATUSES.has(status);
}

export function isReaderWaitingStatus(status: string): boolean {
  return status === 'requires_payment_method' || status === 'requires_source';
}

// A failure on the intent means a card was tapped and declined (within a
// reader-waiting status, `outcome === 'failed'` is exactly the old
// "last_payment_error present" signal — a canceled intent carries a different
// status, handled by isTerminalFailure).
export function hasPaymentFailure(intent: PaymentIntentDto): boolean {
  return intent.outcome === 'failed';
}

// A reader fault (offline, busy) or a canceled intent ends the payment, also
// when the answer of the send shows it. A card failure in that answer can be
// from the last card (see waitForCaptureOrSuccess), thus it is not in this set.
export function isNonRecoverableFailure(intent: PaymentIntentDto): boolean {
  if (intent.outcome !== 'failed') return false;
  const category = intent.failure?.category;
  return category === 'reader_error' || category === 'canceled';
}

// The attempt_id tells two taps apart. A failure with no attempt_id is the
// same when its code is the same.
export function isSameFailure(
  failure: PaymentFailure | null | undefined,
  other: PaymentFailure | null | undefined
): boolean {
  if (!failure || !other) return !failure && !other;
  if (failure.attempt_id || other.attempt_id) {
    return (failure.attempt_id ?? null) === (other.attempt_id ?? null);
  }
  return (
    failure.category === other.category &&
    failure.code === other.code &&
    (failure.decline_code ?? null) === (other.decline_code ?? null)
  );
}
